import React, { useEffect, useMemo, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';

import { loadLanguages, loadTranslates, deleteRequest } from '../utils/Network';
import TranslateFormContent from './TranslateFormContent';
import CustomModal from '../components/shared/CustomModal';

/** Matches backend TranslationResultsSetPagination default page_size. */
const TRANSLATE_PAGE_SIZE = 40;

/** First non-empty value in the same language order as TranslateFormContent. */
function getFirstTranslatePreview(item, languages) {
    const vals = item?.values;
    if (!vals?.length) return '';
    const pick = (v) => (v != null && String(v).trim() ? String(v).trim().replace(/\s+/g, ' ') : '');
    if (languages?.length) {
        for (let i = 0; i < languages.length; i++) {
            const langId = parseInt(languages[i].id, 10);
            const row = vals.find((x) => x.language === langId);
            const text = pick(row?.value);
            if (text) return text;
        }
    }
    for (let j = 0; j < vals.length; j++) {
        const text = pick(vals[j]?.value);
        if (text) return text;
    }
    return '';
}

export default function TranslateContent({ id }) {
    const history = useHistory();
    const location = useLocation();

    const translateListPath = useMemo(
        () =>
            location.pathname.includes('/cms/translations')
                ? '/cms/translations'
                : '/cms/translate',
        [location.pathname]
    );

    const [loading, setLoading] = useState(0);
    const [pageloading, setPageLoading] = useState(false);

    const [languages, setLanguages] = useState(null);
    const [translates, setTranslates] = useState(null);
    const [count, setCount] = useState(null);
    const [nextPage, setNextPage] = useState(2);
    const [loadingMore, setLoadingMore] = useState(false);

    const [modalOpen, setModalOpen] = useState(false);
    const [formNonce, setFormNonce] = useState(0);
    const [searchInput, setSearchInput] = useState('');

    const searchFromUrl = () => {
        const params = new URLSearchParams(location.search);
        return (params.get('search') || '').trim();
    };

    useEffect(() => {
        loadLanguages().then((langs) => {
            setLanguages(langs);
        });
    }, []);

    useEffect(() => {
        const s = searchFromUrl();
        setSearchInput(s);
    }, [location.search]);

    useEffect(() => {
        if (id) {
            setFormNonce((n) => n + 1);
            setModalOpen(true);
        }
    }, [id]);

    const _getTranslates = (search) => {
        setPageLoading(true);
        const q = search !== undefined ? search : searchFromUrl();
        loadTranslates(q, 1, TRANSLATE_PAGE_SIZE).then((data) => {
            if (data.count != null) {
                setCount(data.count);
            }
            if (data.results) {
                setTranslates(data.results);
            } else {
                setTranslates([]);
            }
            setNextPage(2);
            setPageLoading(false);
        });
    };

    const loadMoreTranslates = () => {
        if (loadingMore || pageloading || translates == null || count == null) {
            return;
        }
        if (translates.length >= count) {
            return;
        }
        setLoadingMore(true);
        const q = searchFromUrl();
        loadTranslates(q, nextPage, TRANSLATE_PAGE_SIZE)
            .then((data) => {
                const batch = data.results || [];
                setTranslates((prev) => [...(prev || []), ...batch]);
                setNextPage((p) => p + 1);
                if (data.count != null) {
                    setCount(data.count);
                }
            })
            .finally(() => setLoadingMore(false));
    };

    useEffect(() => {
        _getTranslates();
    }, [location.search]);

    const handleModalClose = () => {
        setModalOpen(false);
        if (id) {
            history.replace('/cms/translate');
        }
    };

    const openNewModal = () => {
        if (id) {
            history.replace('/cms/translate');
        }
        setFormNonce((n) => n + 1);
        setModalOpen(true);
    };

    const handleDelete = (rowId) => {
        setLoading(rowId);

        deleteRequest('api/translate', rowId)
            .then(() => {
                _getTranslates();
            })
            .finally(() => {
                setLoading(0);
            });
    };

    const onFilterSubmit = (e) => {
        e.preventDefault();
        const q = new URLSearchParams();
        if (searchInput.trim()) {
            q.set('search', searchInput.trim());
        }
        const qs = q.toString();
        history.push(qs ? `${translateListPath}?${qs}` : translateListPath);
    };

    const clearSearch = () => {
        setSearchInput('');
        history.push(translateListPath);
    };

    const setModalOpenWrapped = (open) => {
        if (!open) {
            handleModalClose();
        } else {
            setModalOpen(true);
        }
    };

    return (
        <>
            <div className="content-wrapper">
                <section className="content-header">
                    <h3 className="box-title mb-10">
                        <span className="sr-only">Translate</span>
                    </h3>
                    <ol className="breadcrumb">
                        <li>
                            <a href="/cms">
                                <i className="fa fa-dashboard" /> Home
                            </a>
                        </li>
                        <li>
                            <a href="/cms/languages">
                                <i className="fa fa-language" /> Languages
                            </a>
                        </li>
                        <li className="active">Translate</li>
                    </ol>
                </section>

                <section className="content padding-top-sm-0">
                    <div className="row">
                        <div className="col-md-12">
                            <div className="box box-primary">
                                <div className="box-header with-border">
                                    <h3 className="box-title mt-5">
                                        Translations{' '}
                                        {count != null && (
                                            <span style={{ fontSize: 11 }}>({count})</span>
                                        )}
                                    </h3>
                                    <button
                                        type="button"
                                        title="Add new"
                                        className="btn btn-cm margin-r-5 pull-right mt-2p"
                                        onClick={openNewModal}
                                    >
                                        <i className="fa fa-plus" />
                                    </button>
                                    {searchFromUrl() && (
                                        <button
                                            type="button"
                                            className="btn btn-cm margin-r-5 pull-right mt-2p"
                                            onClick={clearSearch}
                                        >
                                            Cancel
                                        </button>
                                    )}
                                    <form
                                        onSubmit={onFilterSubmit}
                                        className="pull-right mt-2p margin-r-5"
                                        style={{ marginBottom: 0 }}
                                    >
                                        <div
                                            className="form-inline"
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 6,
                                                flexWrap: 'wrap',
                                            }}
                                        >
                                            <input
                                                name="search"
                                                className="form-control input-sm"
                                                type="search"
                                                placeholder="Search…"
                                                value={searchInput}
                                                onChange={(e) => setSearchInput(e.target.value)}
                                                autoComplete="off"
                                                style={{ width: 168, maxWidth: '38vw' }}
                                            />
                                            <button
                                                type="submit"
                                                title="Search"
                                                className="btn btn-cm margin-r-5"
                                            >
                                                <i className="fa fa-search" />
                                            </button>
                                        </div>
                                    </form>
                                </div>
                                    <div className="box-body no-padding position-relative">
                                        <table
                                            className={`table table-striped cms-list-table cms-translate-table mb-0${
                                                pageloading ? ' opacity-70' : ''
                                            }`}
                                        >
                                            <thead>
                                                <tr>
                                                    <th>Key</th>
                                                    <th>Translation</th>
                                                    <th className="cms-translate-col-actions" aria-label="Actions" />
                                                </tr>
                                            </thead>
                                            <tbody>
                                                    {!pageloading &&
                                                        translates?.map((item) => {
                                                            const preview =
                                                                getFirstTranslatePreview(
                                                                    item,
                                                                    languages
                                                                );
                                                            return (
                                                            <tr
                                                                key={item.id}
                                                                className={loading === item.id ? 'loading' : ''}
                                                            >
                                                                <td width="50%">
                                                                    <p className="mb-0 kfont">
                                                                        <Link
                                                                            to={`${translateListPath}/${item.id}`}
                                                                            className="link-color"
                                                                        >
                                                                            {item.key}
                                                                        </Link>
                                                                    </p>
                                                                </td>
                                                                <td>
                                                                    <p
                                                                        className="mb-0 kfont"
                                                                        style={{
                                                                            maxWidth: 480,
                                                                            overflow: 'hidden',
                                                                            textOverflow: 'ellipsis',
                                                                            whiteSpace: 'nowrap',
                                                                        }}
                                                                        title={preview || undefined}
                                                                    >
                                                                        {preview ? (
                                                                            <Link
                                                                                to={`${translateListPath}/${item.id}`}
                                                                                className="link-color"
                                                                            >
                                                                                {preview}
                                                                            </Link>
                                                                        ) : (
                                                                            <span className="text-muted">—</span>
                                                                        )}
                                                                    </p>
                                                                </td>
                                                                <td className="cms-translate-col-actions">
                                                                    <div className="btn-group cms-row-actions">
                                                                        {/* <a
                                                                            href={`${translateListPath}/${item.id}`}
                                                                            className="btn btn-cm btn-sm cms-row-action-btn"
                                                                            title="Edit"
                                                                        >
                                                                            <i className="fa fa-edit" />
                                                                        </a> */}
                                                                        <button
                                                                            type="button"
                                                                            title="Delete"
                                                                            onClick={() => {
                                                                                if (
                                                                                    window.confirm(
                                                                                        'Are you sure you wish to delete this item?'
                                                                                    )
                                                                                ) {
                                                                                    handleDelete(item.id);
                                                                                }
                                                                            }}
                                                                            className="btn btn-cm btn-sm cms-row-action-btn cms-row-action-btn-danger"
                                                                        >
                                                                            <i className="fa fa-trash" />
                                                                        </button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                            );
                                                        })}
                                            </tbody>
                                            
                                        </table>
                                        {!pageloading && !translates?.length && (
                                            <div className="padding-10 text-muted">No translations found.</div>
                                        )}
                                        {!pageloading &&
                                            translates?.length > 0 &&
                                            count != null &&
                                            translates.length < count && (
                                                <div className="cms-translate-load-more clearfix">
                                                    <button
                                                        type="button"
                                                        className="btn btn-default"
                                                        disabled={loadingMore}
                                                        onClick={loadMoreTranslates}
                                                    >
                                                        {loadingMore ? 'Loading…' : 'Load more'}
                                                    </button>
                                                    <span className="cms-translate-load-more-meta">
                                                        Showing {translates.length} of {count}
                                                    </span>
                                                </div>
                                            )}
                                    </div>
                                </div>
                            </div>
                        </div>
                </section>
            </div>

            <CustomModal
                title={''}
                showHeader={false}
                isOpen={modalOpen}
                setIsOpen={setModalOpenWrapped}
                modalClassName="Modal--translate-cms"
                bodyClassName="text-left"
            >
                {modalOpen && (
                    <TranslateFormContent
                        key={`translate-form-${id || 'new'}-${formNonce}`}
                        id={id}
                        languages={languages}
                        variant="modal"
                        onCancel={handleModalClose}
                        onSaved={() => {
                            handleModalClose();
                            _getTranslates();
                        }}
                    />
                )}
            </CustomModal>
        </>
    );
}
